/**
 * projectMoneyProfile.ts — «денежный профиль проекта» (WEB-2126).
 *
 * ОДНА правда для всех экранов кабинета. Два признака:
 *   · `ownMoney` — владелец выбрал «сам, у себя»: деньги идут мимо нас, и
 *     денежные разделы кабинета не наполнятся никогда;
 *   · `noBridge` — выбран путь «приложение не подключаю»: мостовых веток нет,
 *     остаются ключи и события на сервер клиента.
 *
 * ПОЧЕМУ ОТДЕЛЬНЫЙ ФАЙЛ, А НЕ ПО ВЫЧИСЛЕНИЮ НА ЭКРАН. Признаки читают меню,
 * отчёты, «Магазин», «Платежи», «Подключение приложения» и мастер создания
 * проекта. Набранное в каждом месте своё сравнение разъедется на первой же
 * правке (шрам WEB-1687: ручной список молчит про новое значение по
 * построению), и разъедется на деньгах.
 *
 * FAIL-CLOSED. `ownMoney` истинен строго при `=== true`. Объект проекта,
 * загруженный старой сборкой или ещё не приехавший, читается как «через нас»:
 * лишний денежный раздел на экране — мелкая неприятность, спрятанный у
 * клиента, который через нас продаёт, — потерянное управление ценами.
 *
 * ОБЩИЙ КОНФИГ МОСТА. `noBridge` живёт не в объекте проекта, а в конфиге
 * моста (`GET /projects/:id/bridge-config`). За ним ходят сразу двое:
 * страница настроек (от признака зависит НАЗВАНИЕ вкладки, а его надо знать
 * до того, как вкладку открыли) и сам раздел «Подключение приложения».
 * Поэтому поход за конфигом здесь один на проект: кто первый спросил, тот и
 * запустил запрос, остальные ждут тот же ответ. Выбор пути публикуется сюда
 * же (`publishBridgeConfig`) — иначе название вкладки осталось бы прежним до
 * перезагрузки страницы.
 */
import { bridgeApi, type BridgeConfig } from '@/api/bridge';

/**
 * Способ подключения «приложение не подключаю».
 *
 * Зеркало серверного перечисления `CONNECTION_METHODS`
 * (`backend/src/projects/schemas/bridge-config.schema.ts`). Строкой в коде не
 * набирается: сравнение по литералу — ровно тот случай, когда одна из копий
 * молча остаётся старой.
 */
export const NO_BRIDGE_CONNECTION_METHOD = 'none';

/** Что от объекта проекта нужно профилю — не весь `Project`. */
export interface MoneyProfileProject {
  ownPayments?: boolean;
}

/** Что от конфига моста нужно профилю. */
export interface MoneyProfileBridge {
  connectionMethod?: string | null;
}

export interface ProjectMoneyProfile {
  /** Деньги принимает сам клиент, у себя. */
  ownMoney: boolean;
  /** Приложение с квизами не связывается. */
  noBridge: boolean;
}

/** Деньги у клиента? Строго `=== true`, см. fail-closed в докблоке. */
export function isOwnMoney(project?: MoneyProfileProject | null): boolean {
  return project?.ownPayments === true;
}

/** Путь «приложение не подключаю»? Только точное значение способа. */
export function isNoBridge(config?: MoneyProfileBridge | null): boolean {
  return config?.connectionMethod === NO_BRIDGE_CONNECTION_METHOD;
}

/** Оба признака разом — то, что просят экраны. */
export function projectMoneyProfile(
  project?: MoneyProfileProject | null,
  config?: MoneyProfileBridge | null,
): ProjectMoneyProfile {
  return { ownMoney: isOwnMoney(project), noBridge: isNoBridge(config) };
}

// ─── общий конфиг моста: один поход на проект ───────────────────────────────

const configs = new Map<string, BridgeConfig>();
const inFlight = new Map<string, Promise<BridgeConfig>>();
const listeners = new Set<() => void>();

function notify(): void {
  listeners.forEach((fn) => fn());
}

/** Прочитанный конфиг проекта; `undefined` — ещё не читали или не прочли. */
export function readBridgeConfig(projectId: string): BridgeConfig | undefined {
  return configs.get(projectId);
}

/**
 * Положить в общий кэш уже известный конфиг.
 *
 * Зовётся после записи способа подключения: свежий ответ ручки уже на руках,
 * и второй поход за ним был бы и лишним запросом, и мигающим названием
 * вкладки.
 */
export function publishBridgeConfig(projectId: string, config: BridgeConfig): void {
  configs.set(projectId, config);
  notify();
}

/** Подписка на изменения кэша (для хука). Возвращает отписку. */
export function subscribeBridgeConfig(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Конфиг моста проекта: из кэша либо одним общим запросом.
 *
 * ⚠ Отказ НЕ запоминается: следующий спрашивающий пойдёт на сервер заново.
 * Запомнить «не получилось» значило бы на один сетевой сбой признать проект
 * бесконечно «с мостом» и показать не то название вкладки до перезагрузки.
 */
export function ensureBridgeConfig(projectId: string): Promise<BridgeConfig> {
  const cached = configs.get(projectId);
  if (cached) return Promise.resolve(cached);
  const running = inFlight.get(projectId);
  if (running) return running;
  const request = bridgeApi
    .getConfig(projectId)
    .then((res) => {
      const config = (res?.data ?? {}) as BridgeConfig;
      configs.set(projectId, config);
      inFlight.delete(projectId);
      notify();
      return config;
    })
    .catch((err) => {
      inFlight.delete(projectId);
      throw err;
    });
  inFlight.set(projectId, request);
  return request;
}

/** Только для тестов: кэш общий на модуль и переживает наборы. */
export function resetBridgeConfigCache(): void {
  configs.clear();
  inFlight.clear();
  listeners.clear();
}
